import { PageHeader } from "@/components/ui/design-system";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { cijeliBroj, datum } from "@/lib/format";
import { porukaDohvata } from "@/lib/greske";
import { prebroj, rupeUStavke, stavkeZaPaznju, type Stavka } from "@/lib/paznja";
import {
  dohvatiRadnike,
  dohvatiZapisnik,
  type Radnik,
  type StavkaZapisnika,
} from "@/lib/radnici";
import { dohvatiRupe, type Rupa } from "@/lib/sazetak";
import { cn } from "@/lib/utils";

/**
 * The personnel dashboard — `docs/dizajn/ekran-pocetna.dc.html`.
 *
 * One question: what needs doing today. The counters say how much, the list
 * says who and what, and every row carries the verb that resolves it.
 *
 * The whole workforce is fetched in one request rather than summarised by a
 * dedicated endpoint. Eighty-five people is one page; a summary endpoint would
 * be a second place where the same thresholds are implemented, and two
 * implementations of a regulatory threshold eventually disagree.
 */

const CIJELA_EVIDENCIJA = 200;

/**
 * When a missing day reaches this dashboard (O-18).
 *
 * The server holds the same number in configuration and applies it too; this
 * one only says which of the two escalation steps this screen is asking about.
 */
const PRAG_RUPA_KADROVSKA = 5;

export function PocetnaKadrovskaPage() {
  const [radnici, setRadnici] = useState<Radnik[] | null>(null);
  const [zapisnik, setZapisnik] = useState<StavkaZapisnika[] | null>(null);
  const [rupe, setRupe] = useState<Rupa[]>([]);
  const [greska, setGreska] = useState<string | null>(null);

  useEffect(() => {
    const kontroler = new AbortController();

    dohvatiRadnike(
      { limit: CIJELA_EVIDENCIJA, samo_aktivni: true },
      kontroler.signal,
    )
      .then((odgovor) => setRadnici(odgovor.items))
      .catch((error: unknown) => {
        if (kontroler.signal.aborted) return;
        setGreska(porukaDohvata(error));
        console.error(error);
      });

    // The feed is a nicety; if the account may not read the audit log, the
    // rest of the screen still works.
    dohvatiZapisnik(8, kontroler.signal)
      .then(setZapisnik)
      .catch(() => setZapisnik([]));

    // Days whose hours never arrived, from the fifth day (O-18). Personnel
    // are the second step of that escalation: the manager has already had two
    // days to notice on his own screen.
    dohvatiRupe(PRAG_RUPA_KADROVSKA, kontroler.signal)
      .then(setRupe)
      .catch(() => setRupe([]));

    return () => kontroler.abort();
  }, []);

  // The heading is outside every branch on purpose: it names the screen
  // whether the data has arrived, failed or is still on its way, so the page
  // does not appear to be a different page while it loads.
  const naslov = (
    <PageHeader title={<>Kadrovska</>} />
  );

  if (greska) {
    return (
      <div className="flex flex-col gap-8">
        {naslov}
        <EmptyState naslov="Početna se ne može učitati" objasnjenje={greska} />
      </div>
    );
  }

  if (!radnici) {
    return (
      <div className="flex flex-col gap-8">
        {naslov}
        <SkeletonTablica redaka={3} />
      </div>
    );
  }

  const brojevi = prebroj(radnici);
  const paznja = [...stavkeZaPaznju(radnici), ...rupeUStavke(rupe)].sort(
    (a, b) => {
      if (a.blokira !== b.blokira) return a.blokira ? -1 : 1;
      return (a.dana ?? 9999) - (b.dana ?? 9999);
    },
  );

  return (
    <div className="flex flex-col gap-8">
      {naslov}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Brojka naziv="Aktivni zaposlenici" vrijednost={brojevi.aktivni} />
        <Brojka
          naziv="Upozorenja"
          vrijednost={brojevi.upozorenja}
          ton={brojevi.upozorenja > 0 ? "warn" : undefined}
        />
        <Brojka
          naziv="Kritično"
          vrijednost={brojevi.kriticno}
          ton={brojevi.kriticno > 0 ? "bad" : undefined}
        />
        <Brojka naziv="Nepotpuni profili" vrijednost={brojevi.nepotpuni} />
      </section>

      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <section className="flex flex-col gap-3">
          <h2 className="text-[var(--text-section)] font-semibold">
            Zahtijeva pažnju
          </h2>
          {paznja.length === 0 ? (
            <div className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
              <EmptyState
                naslov="Nema ničega za riješiti"
                objasnjenje="Sve dozvole, liječnički pregledi i ugovori su u roku."
              />
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {paznja.map((stavka, index) => (
                <RedakPaznje key={`${stavka.radnikId}-${index}`} stavka={stavka} />
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-[var(--text-section)] font-semibold">Nedavno</h2>
          {!zapisnik ? (
            <SkeletonTablica redaka={2} />
          ) : zapisnik.length === 0 ? (
            <p className="text-[var(--color-muted-foreground)]">
              Još nema zabilježenih izmjena.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {zapisnik.map((stavka) => (
                <li
                  key={stavka.id}
                  className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4"
                >
                  <p>
                    {stavka.tko ?? "Sustav"} — {stavka.radnja} ({stavka.entitet}
                    {stavka.radnik ? `: ${stavka.radnik}` : ""})
                  </p>
                  {/* Field names only. The log holds the values; a screen
                      people walk past does not. */}
                  {stavka.polja.length > 0 ? (
                    <p className="text-[var(--text-small)] text-[var(--color-muted-foreground)]">
                      Polja: {stavka.polja.join(", ")}
                    </p>
                  ) : null}
                  <p className="broj text-[var(--text-small)] text-[var(--color-muted-foreground)]">
                    {datum(stavka.kada)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Brojka({
  naziv,
  vrijednost,
  ton,
}: {
  naziv: string;
  vrijednost: number;
  ton?: "warn" | "bad";
}) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6",
        ton === "warn" && "bg-[var(--color-status-warn-bg)]",
        ton === "bad" && "bg-[var(--color-status-bad-bg)]",
      )}
    >
      <p className="oznaka">{naziv}</p>
      <p
        className={cn(
          "broj mt-2 text-[var(--text-title)] font-bold",
          ton === "warn" && "text-[var(--color-status-warn-fg)]",
          ton === "bad" && "text-[var(--color-status-bad-fg)]",
        )}
      >
        {cijeliBroj(vrijednost)}
      </p>
    </div>
  );
}

function RedakPaznje({ stavka }: { stavka: Stavka }) {
  const rok =
    stavka.dana === null
      ? null
      : stavka.dana < 0
        ? `prije ${Math.abs(stavka.dana)} d.`
        : stavka.dana === 0
          ? "danas"
          : `za ${stavka.dana} d.`;

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4",
        stavka.blokira && "bg-[var(--color-bad-row)]",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">{stavka.radnik}</p>
        <p className="text-[var(--color-muted-foreground)]">{stavka.poruka}</p>
      </div>
      {rok ? (
        <Badge ton={stavka.blokira ? "bad" : "warn"}>
          <span className="broj">{rok}</span>
        </Badge>
      ) : (
        <Badge ton={stavka.blokira ? "bad" : "idle"}>
          {stavka.blokira ? "Blokira rad" : "Dopuniti"}
        </Badge>
      )}
      {/* The verb, written out. No hidden menu, no icon on its own. */}
      <Link
        to={stavka.poveznica ?? `/zaposlenici/${stavka.radnikId}`}
        className="font-medium underline underline-offset-2"
      >
        {stavka.akcija ?? "Otvori karton"}
      </Link>
    </li>
  );
}
